import {
	type Action,
	type BoundActions,
	createHook,
	createStore,
	type HookFunction,
} from 'react-sweet-state';

import {
	type AppConfigStoreState as AppConfigStoreState,
	type AppName,
	type EmbedConfig,
	type TeamsClientConfig,
	type TeamsNavigationConfig,
	type TeamsPreviewPanelConfig,
	type TeamsProductConfig,
} from '../types';

const defaultNavigationState: TeamsNavigationConfig = {
	contextSpaPaths: [],
	contextEntryPoint: null,
};

const defaultClientConfigState: TeamsClientConfig = {
	cloudId: null,
	stargateRoot: '',
	env: undefined,
};

const defaultTeamsProductConfigState: TeamsProductConfig = {
	appName: null,
	productName: null,
	isJiraAvailable: false,
	openLinksInNewTab: false,
};

const defaultTeamsPreviewPanelConfigState: TeamsPreviewPanelConfig = {
	openPreviewPanel: () => {},
};

const defaultEmbedConfigState: EmbedConfig = {
	openGiveKudosInNewTab: false,
};

const defaultState: AppConfigStoreState = {
	...defaultNavigationState,
	...defaultClientConfigState,
	...defaultTeamsProductConfigState,
	...defaultTeamsPreviewPanelConfigState,
	...defaultEmbedConfigState,
	docTitle: {},
};

const actions = {
	setNavigationConfig:
		(navigationConfig: TeamsNavigationConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...navigationConfig,
			});
		},
	setClientConfig:
		(clientConfig: TeamsClientConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...clientConfig,
			});
		},
	setTeamsProductConfig:
		(teamsProductConfig: TeamsProductConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...teamsProductConfig,
			});
		},
	setTeamsPreviewPanelConfig:
		(teamsPreviewPanelConfig: TeamsPreviewPanelConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...teamsPreviewPanelConfig,
			});
		},
	setEmbedConfig:
		(embedConfig: EmbedConfig): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...embedConfig,
			});
		},
	// Set all config at once
	setAppConfig:
		(appConfig: AppConfigStoreState): Action<AppConfigStoreState> =>
		({ setState }) => {
			setState({
				...appConfig,
			});
		},
};

type AppConfigActions = typeof actions;

const AppConfigStore = createStore<AppConfigStoreState, AppConfigActions>({
	initialState: defaultState,
	actions,
});

export const useAppConfigStore: HookFunction<
	AppConfigStoreState,
	BoundActions<
		AppConfigStoreState,
		{
			setNavigationConfig: (navigationConfig: TeamsNavigationConfig) => Action<AppConfigStoreState>;
			setClientConfig: (clientConfig: TeamsClientConfig) => Action<AppConfigStoreState>;
			setTeamsProductConfig: (
				teamsProductConfig: TeamsProductConfig,
			) => Action<AppConfigStoreState>;
			setTeamsPreviewPanelConfig: (
				teamsPreviewPanelConfig: TeamsPreviewPanelConfig,
			) => Action<AppConfigStoreState>;
			setEmbedConfig: (embedConfig: EmbedConfig) => Action<AppConfigStoreState>;
			// Set all config at once
			setAppConfig: (appConfig: AppConfigStoreState) => Action<AppConfigStoreState>;
		}
	>,
	void
> = createHook(AppConfigStore);
const useAppConfigActions = createHook(AppConfigStore, { selector: null });

export const useAppConfigStoreState = (): AppConfigStoreState => useAppConfigStore()[0];

export const useSetTeamsConfigActions = (): BoundActions<
	AppConfigStoreState,
	{
		setNavigationConfig: (navigationConfig: TeamsNavigationConfig) => Action<AppConfigStoreState>;
		setClientConfig: (clientConfig: TeamsClientConfig) => Action<AppConfigStoreState>;
		setTeamsProductConfig: (teamsProductConfig: TeamsProductConfig) => Action<AppConfigStoreState>;
		setTeamsPreviewPanelConfig: (
			teamsPreviewPanelConfig: TeamsPreviewPanelConfig,
		) => Action<AppConfigStoreState>;
		setEmbedConfig: (embedConfig: EmbedConfig) => Action<AppConfigStoreState>;
		// Set all config at once
		setAppConfig: (appConfig: AppConfigStoreState) => Action<AppConfigStoreState>;
	}
> => {
	return useAppConfigActions()[1];
};

export const useAppName = (): AppName | undefined => useAppConfigStoreState().appName;

export const useNavigationState = (): {
	contextSpaPaths: string[] | undefined;
	contextEntryPoint: string | null | undefined;
} => {
	const { contextSpaPaths, contextEntryPoint } = useAppConfigStoreState();
	return { contextSpaPaths, contextEntryPoint };
};
